import { useEffect, useState } from 'react'
import moment from 'moment'
import { wtApiCall } from './ApiWrapper'
import { wtGetFromLocalStorage, wtSaveToLocalStorage } from './LocalStorage'

const DEFAULT_DATE_FORMAT = 'Y-m-d'
const DEFAULT_DATE_TIME_FORMAT = 'Y-m-d H:i:s'
const DATE_FORMAT_STORAGE_KEY = 'master_date_format'
const DATE_TIME_FORMAT_STORAGE_KEY = 'master_date_time_format'
export const MASTER_DATE_FORMATS_UPDATED_EVENT = 'master-date-formats-updated'

const CONFIG_KEYS = {
  dateFormat: ['date_format'],
  dateTimeFormat: ['date_time_format', 'datetime_format'],
}

const phpToMomentTokenMap = {
  Y: 'YYYY',
  y: 'YY',
  m: 'MM',
  n: 'M',
  d: 'DD',
  j: 'D',
  H: 'HH',
  h: 'hh',
  i: 'mm',
  s: 'ss',
  A: 'A',
  a: 'a',
}

const findConfig = (rows, keys) => rows.find((row) => keys.includes(row.key))

export const saveMasterDateFormatsToStorage = ({ dateFormat, dateTimeFormat }) => {
  wtSaveToLocalStorage(DATE_FORMAT_STORAGE_KEY, dateFormat || DEFAULT_DATE_FORMAT)
  wtSaveToLocalStorage(DATE_TIME_FORMAT_STORAGE_KEY, dateTimeFormat || DEFAULT_DATE_TIME_FORMAT)

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(MASTER_DATE_FORMATS_UPDATED_EVENT, {
        detail: {
          dateFormat: dateFormat || DEFAULT_DATE_FORMAT,
          dateTimeFormat: dateTimeFormat || DEFAULT_DATE_TIME_FORMAT,
        },
      }),
    )
  }
}

export const phpFormatToMoment = (phpFormat = '') =>
  String(phpFormat)
    .split('')
    .map((char) => phpToMomentTokenMap[char] || char)
    .join('')

export const formatWithMasterPattern = (value, phpFormat, fallback = '') => {
  if (!value) {
    return fallback
  }

  const momentFormat = phpFormatToMoment(phpFormat)
  const parsedValue = moment(value)

  if (!parsedValue.isValid()) {
    return fallback || value
  }

  return parsedValue.format(momentFormat)
}

export const phpFormatToExcel = (phpFormat = '') =>
  String(phpFormat).replace(/[Yymndj]/g, (token) => {
    switch (token) {
      case 'Y':
        return 'yyyy'
      case 'y':
        return 'yy'
      case 'm':
        return 'mm'
      case 'n':
        return 'm'
      case 'd':
        return 'dd'
      case 'j':
        return 'd'
      default:
        return token
    }
  })

export const getExcelDateExportConfig = (value, phpFormat, fallback = '') => {
  if (!value) {
    return {
      type: 'empty',
      value: '',
      numberFormat: phpFormatToExcel(phpFormat),
    }
  }

  const parsedValue = moment(value)
  if (!parsedValue.isValid()) {
    return {
      type: 'str',
      value: formatWithMasterPattern(value, phpFormat, fallback),
      numberFormat: phpFormatToExcel(phpFormat),
    }
  }

  return {
    type: 'num',
    value: parsedValue.format('YYYY-MM-DD'),
    numberFormat: phpFormatToExcel(phpFormat),
  }
}

export const useMasterDateTimeFormats = () => {
  const [dateFormat, setDateFormat] = useState(
    wtGetFromLocalStorage(DATE_FORMAT_STORAGE_KEY, DEFAULT_DATE_FORMAT),
  )
  const [dateTimeFormat, setDateTimeFormat] = useState(
    wtGetFromLocalStorage(DATE_TIME_FORMAT_STORAGE_KEY, DEFAULT_DATE_TIME_FORMAT),
  )

  useEffect(() => {
    wtApiCall(
      'system_configurations',
      {
        _page_index: 1,
        _page_size: 200,
      },
      'get',
      (response) => {
        const rows = response?.data?.data || []
        const loadedDateFormat =
          findConfig(rows, CONFIG_KEYS.dateFormat)?.value || DEFAULT_DATE_FORMAT
        const loadedDateTimeFormat =
          findConfig(rows, CONFIG_KEYS.dateTimeFormat)?.value || DEFAULT_DATE_TIME_FORMAT

        setDateFormat(loadedDateFormat)
        setDateTimeFormat(loadedDateTimeFormat)
        saveMasterDateFormatsToStorage({
          dateFormat: loadedDateFormat,
          dateTimeFormat: loadedDateTimeFormat,
        })
      },
      () => {},
    )
    const syncFromEvent = (event) => {
      const nextDateFormat = event?.detail?.dateFormat || DEFAULT_DATE_FORMAT
      const nextDateTimeFormat = event?.detail?.dateTimeFormat || DEFAULT_DATE_TIME_FORMAT
      setDateFormat(nextDateFormat)
      setDateTimeFormat(nextDateTimeFormat)
    }

    window.addEventListener(MASTER_DATE_FORMATS_UPDATED_EVENT, syncFromEvent)

    return () => {
      window.removeEventListener(MASTER_DATE_FORMATS_UPDATED_EVENT, syncFromEvent)
    }
  }, [])

  return {
    dateFormat,
    dateTimeFormat,
    momentDateFormat: phpFormatToMoment(dateFormat),
    momentDateTimeFormat: phpFormatToMoment(dateTimeFormat),
    formatDate: (value, fallback = '') => formatWithMasterPattern(value, dateFormat, fallback),
    formatDateTime: (value, fallback = '') =>
      formatWithMasterPattern(value, dateTimeFormat, fallback),
  }
}
